import { useRef, useEffect, useCallback } from 'react'

/**
 * KnurledWheel — a tactile, photoreal diamond-faceted wheel that replaces a
 * number input's spinner arrows. Drag up/down, scroll, or arrow-key to step.
 *
 * Controlled: pass `value` (number) and `onChange(nextNumber)`. The wheel only
 * renders the facets (value lives in your input), so value changes don't redraw
 * the canvas — only rotation does.
 *
 * Rendering matches the reference photo:
 *  - ONE diamond spans the full wheel width (big low-poly facets), height ≈ 1.2× width
 *  - Facets are shaded in GRAYSCALE in the tile (soft satin light from top-left)
 *  - The gold → orange → rust → dark-brown gradient is applied in render() as a
 *    'multiply' pass FIXED to the frame — so the facets roll under the light,
 *    but the color grade stays put, exactly like a real anodized wheel.
 *
 *   <KnurledWheel
 *     value={parseFloat(qty) || 0}
 *     onChange={(v) => setQty(String(v))}
 *     step={0.001} min={0} height={52} ariaLabel="Quantity" />
 */
export default function KnurledWheel({
  value = 0,
  onChange,
  step = 0.001,
  min = 0,
  max = Infinity,
  snap = true,
  width = 34,
  height = 52,
  pxPerStep = 4,
  ariaLabel = 'Value',
  className = '',
  style = {},
}) {
  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const tileRef = useRef(null)
  const dimsRef = useRef({ cw: 0, ch: 0, patH: 0, dpr: 1 })
  const phaseRef = useRef(0)
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  const cfgRef = useRef({ step, min, max, snap, pxPerStep })

  // Keep refs current so native event handlers always see the latest props.
  valueRef.current = value
  onChangeRef.current = onChange
  cfgRef.current = { step, min, max, snap, pxPerStep }

  // Build the facet tile in GRAYSCALE (color is graded later, fixed to frame).
  // Big pyramid diamonds: one full diamond across the width, ~1.2× taller than
  // wide, apex at the center of each diamond — matching the reference photo's
  // bold low-poly geometry. Tileable vertically for seamless rolling.
  const buildTile = useCallback(() => {
    const d = dimsRef.current
    const cw = d.cw
    if (!cw) return

    // ── Triangulated relief (fresh build to match the photo's structure) ──
    // Staggered triangular vertex lattice. PEAK vertices sit at hexagon centers:
    // six triangles fan off each peak, forming the hexagons. Every other vertex
    // is a low (valley) vertex, so the regions between hexagons read as
    // diamond-shaped valleys — high point = hexagon center, valleys on the
    // diamonds. Each triangle is a flat plane shaded by its true normal, and
    // valley heights are jittered so no two triangles match (low-poly, not lattice).
    const sx = cw / 2                                // horizontal vertex spacing → hexagon spans full width
    const sy = Math.max(4, Math.round(cw * 0.6))     // row spacing → hexagon height ≈ 1.2× width
    const ROWS = 4                                   // rows per tile = 2 hexagon rows with independent jitter
    const patH = sy * ROWS
    d.patH = patH

    const tile = document.createElement('canvas')
    tile.width = cw
    tile.height = patH
    const tc = tile.getContext('2d')
    if (!tc) return
    const img = tc.createImageData(cw, patH)
    const data = img.data

    // Light: right/top bias — highlights on the right faces, shadows on the left.
    let Lx = 0.44, Ly = -0.30, Lz = 0.84
    const Ll = Math.sqrt(Lx * Lx + Ly * Ly + Lz * Lz); Lx /= Ll; Ly /= Ll; Lz /= Ll
    let Hx = Lx, Hy = Ly, Hz = Lz + 1
    const Hl = Math.sqrt(Hx * Hx + Hy * Hy + Hz * Hz); Hx /= Hl; Hy /= Hl; Hz /= Hl

    const AMP = 0.5 * cw // relief amplitude (height units are pixels, like x/y)
    const SS = 2         // 2×2 supersampling: clean triangle edges at small sizes

    // Vertex hash, periodic in the tile (row index mod ROWS) so scrolling is seamless.
    const vRnd = (c, r, seed) => {
      const rr = ((r % ROWS) + ROWS) % ROWS
      const t = Math.sin(c * 127.1 + rr * 311.7 + seed * 74.7) * 43758.5453
      return t - Math.floor(t)
    }
    // Vertex height: peaks (hexagon centers) on even rows at odd columns —
    // odd columns put the peak column at x = W/2, hexagon centered on the wheel.
    const vHeight = (c, r) => {
      const rr = ((r % ROWS) + ROWS) % ROWS
      const isPeak = (rr % 2 === 0) && (((c % 2) + 2) % 2 === 1)
      return isPeak
        ? 0.88 + vRnd(c, r, 1) * 0.22   // peaks ~0.88–1.10
        : 0.06 + vRnd(c, r, 2) * 0.34   // valleys ~0.06–0.40, jittered → irregular triangles
    }
    const vX = (c, r) => (c + 0.5 * (((r % 2) + 2) % 2)) * sx

    for (let yPix = 0; yPix < patH; yPix++) {
      for (let xPix = 0; xPix < cw; xPix++) {
        let acc = 0
        for (let ssy = 0; ssy < SS; ssy++) {
          for (let ssx = 0; ssx < SS; ssx++) {
            const x = xPix + (ssx + 0.5) / SS
            const y = yPix + (ssy + 0.5) / SS
            // ── Locate the containing triangle in the staggered strip ──
            const r = Math.floor(y / sy)
            const ty = y / sy - r                       // 0 at top row, 1 at bottom row
            const o = 0.5 * (((r % 2) + 2) % 2)         // top-row x offset (in columns)
            const xl = x / sx - o                       // top verts at integers, bottoms at half-integers
            const i = Math.floor(xl)
            const f = xl - i
            // bottom vertex at xl = i + 0.5 has column index:
            const cb = (((r % 2) + 2) % 2 === 0) ? i : i + 1
            let c1, r1, c2, r2, c3, r3
            if (ty <= 2 * f && ty <= 2 * (1 - f)) {
              // Down-triangle: top edge (i, i+1), bottom apex
              c1 = i; r1 = r; c2 = i + 1; r2 = r; c3 = cb; r3 = r + 1
            } else if (f < 0.5 * ty) {
              // Up-triangle, apex = top vertex i
              c1 = i; r1 = r; c2 = cb - 1; r2 = r + 1; c3 = cb; r3 = r + 1
            } else {
              // Up-triangle, apex = top vertex i+1
              c1 = i + 1; r1 = r; c2 = cb; r2 = r + 1; c3 = cb + 1; r3 = r + 1
            }
            // ── Flat shade from the triangle's true plane normal ──
            const x1 = vX(c1, r1), y1 = r1 * sy, z1 = vHeight(c1, r1) * AMP
            const x2 = vX(c2, r2), y2 = r2 * sy, z2 = vHeight(c2, r2) * AMP
            const x3 = vX(c3, r3), y3 = r3 * sy, z3 = vHeight(c3, r3) * AMP
            let nx = (y2 - y1) * (z3 - z1) - (z2 - z1) * (y3 - y1)
            let ny = (z2 - z1) * (x3 - x1) - (x2 - x1) * (z3 - z1)
            let nz = (x2 - x1) * (y3 - y1) - (y2 - y1) * (x3 - x1)
            if (nz < 0) { nx = -nx; ny = -ny; nz = -nz }
            const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1
            nx /= nl; ny /= nl; nz /= nl
            let diff = nx * Lx + ny * Ly + nz * Lz; if (diff < 0) diff = 0
            let sp = nx * Hx + ny * Hy + nz * Hz; if (sp < 0) sp = 0
            sp = Math.pow(sp, 8) * 0.14 // broad, soft satin sheen
            let I = 0.22 + 0.74 * diff + sp
            if (I > 1) I = 1
            acc += I
          }
        }
        const g = Math.round(255 * (acc / (SS * SS)))
        const idx = (yPix * cw + xPix) * 4
        data[idx] = g; data[idx + 1] = g; data[idx + 2] = g; data[idx + 3] = 255
      }
    }
    tc.putImageData(img, 0, 0)
    tileRef.current = tile
  }, [])

  // Draw: (1) grayscale facets scrolled by phase → the wheel visibly rolls,
  // (2) the photo's vertical color grade multiplied on top, FIXED to the frame,
  // (3) a soft top sheen. Light and color stay put; only the metal moves.
  const render = useCallback(() => {
    const canvas = canvasRef.current
    const tile = tileRef.current
    const d = dimsRef.current
    if (!canvas || !tile || !d.cw || !d.ch) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const { cw, ch, patH } = d
    ctx.globalCompositeOperation = 'source-over'
    ctx.globalAlpha = 1
    ctx.clearRect(0, 0, cw, ch)

    // 1) Facet pass — scrolls with drag / wheel / keys
    const pattern = ctx.createPattern(tile, 'repeat')
    if (pattern) {
      const offsetY = ((-phaseRef.current % patH) + patH) % patH
      ctx.save()
      ctx.translate(0, offsetY)
      ctx.fillStyle = pattern
      ctx.fillRect(0, -offsetY - patH, cw, ch + patH * 2)
      ctx.restore()
    }

    // 2) Color grade — gunmetal, lifted so the lower half doesn't crush facets to black
    ctx.globalCompositeOperation = 'multiply'
    const grade = ctx.createLinearGradient(0, 0, 0, ch)
    grade.addColorStop(0.00, '#101011') // top — medium dark
    grade.addColorStop(0.25, '#a2a9b6')
    grade.addColorStop(0.48, '#ffffff') // centered highlight band
    grade.addColorStop(0.72, '#8f95a1')
    grade.addColorStop(1.00, '#202124') // bottom charcoal (kept darkest, grounds the wheel)
    ctx.fillStyle = grade
    ctx.fillRect(0, 0, cw, ch)

    // 3) Convex body — cylinder-style falloff fixed to the frame: crown just
    //    left of center, both edges fall away. THE cue that makes the wheel
    //    read convex (bulging out) instead of a flat quilted panel.
    const body = ctx.createLinearGradient(0, 0, cw, 0)
    body.addColorStop(0.00, 'rgb(110,110,112)')
    body.addColorStop(0.30, 'rgb(140,140,141)')
    body.addColorStop(0.58, 'rgb(165,165,166)')
    body.addColorStop(0.82, 'rgb(132,132,133)')
    body.addColorStop(1.00, 'rgb(105,105,107)')
    ctx.fillStyle = body
    ctx.fillRect(0, 0, cw, ch)

    // 4) Soft sheen at the very top (satin finish catching the light)
    ctx.globalCompositeOperation = 'screen'
    const sheen = ctx.createLinearGradient(0, 0, 0, ch)
    sheen.addColorStop(0, 'rgba(255,244,210,0.12)')
    sheen.addColorStop(0.25, 'rgba(255,255,255,0)')
    sheen.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = sheen
    ctx.fillRect(0, 0, cw, ch)

    ctx.globalCompositeOperation = 'source-over'
  }, [])

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return
    // Internal density pinned at 2×: on dpr-1 displays the facet features
    // (1-2px ridges/seams) fall below one raster pixel and smear into pure
    // gradient. Rendering 2× and letting CSS downscale keeps them crisp.
    const dpr = 2
    // Size the raster to the CANVAS's box (wrap minus the 2px inset each side),
    // not the wrap's — the old mismatch downscaled the whole render ~20% with
    // bilinear filtering, blurring the facets.
    const cw = Math.round((wrap.clientWidth - 4) * dpr)
    const ch = Math.round((wrap.clientHeight - 4) * dpr)
    if (cw <= 0 || ch <= 0) return
    const d = dimsRef.current
    d.cw = cw; d.ch = ch; d.dpr = dpr
    canvas.width = cw; canvas.height = ch
    buildTile()
    render()
  }, [buildTile, render])

  const applyStep = useCallback((dir) => {
    const { step: st, min: mn, max: mx, snap: sn } = cfgRef.current
    const cur = Number(valueRef.current) || 0
    let next = sn ? Math.round((cur + dir * st) / st) * st : (cur + dir * st)
    next = Math.min(mx, Math.max(mn, next))
    next = Number(next.toFixed(10)) // kill float dust
    if (onChangeRef.current) onChangeRef.current(next)
  }, [])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return

    resize()

    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => resize())
      ro.observe(wrap)
    }

    let dragging = false, lastY = 0, accum = 0

    const onPointerDown = (ev) => {
      dragging = true; lastY = ev.clientY
      try { wrap.setPointerCapture(ev.pointerId) } catch (e) { /* noop */ }
    }
    const onPointerMove = (ev) => {
      if (!dragging) return
      const dy = lastY - ev.clientY; lastY = ev.clientY
      phaseRef.current += dy * 0.6
      accum += dy
      const px = cfgRef.current.pxPerStep
      while (Math.abs(accum) >= px) { const dir = accum > 0 ? 1 : -1; applyStep(dir); accum -= dir * px }
      render()
    }
    const onPointerUp = () => { dragging = false }
    const onWheel = (ev) => {
      ev.preventDefault()
      const dir = ev.deltaY < 0 ? 1 : -1
      applyStep(dir); phaseRef.current += dir * 2.4; render()
    }
    const onKeyDown = (ev) => {
      if (ev.key === 'ArrowUp') { applyStep(1); phaseRef.current += 2.4; render(); ev.preventDefault() }
      else if (ev.key === 'ArrowDown') { applyStep(-1); phaseRef.current -= 2.4; render(); ev.preventDefault() }
    }

    wrap.addEventListener('pointerdown', onPointerDown)
    wrap.addEventListener('pointermove', onPointerMove)
    wrap.addEventListener('pointerup', onPointerUp)
    wrap.addEventListener('pointercancel', onPointerUp)
    wrap.addEventListener('wheel', onWheel, { passive: false })
    wrap.addEventListener('keydown', onKeyDown)

    return () => {
      if (ro) ro.disconnect()
      wrap.removeEventListener('pointerdown', onPointerDown)
      wrap.removeEventListener('pointermove', onPointerMove)
      wrap.removeEventListener('pointerup', onPointerUp)
      wrap.removeEventListener('pointercancel', onPointerUp)
      wrap.removeEventListener('wheel', onWheel)
      wrap.removeEventListener('keydown', onKeyDown)
    }
  }, [resize, render, applyStep])

  const frameRadius = Math.round(Math.min(width, height) * 0.32)

  return (
    <div
      ref={wrapRef}
      role="slider"
      aria-label={ariaLabel}
      aria-valuenow={Number(value)}
      tabIndex={0}
      className={className}
      style={{
        position: 'relative',
        width,
        height,
        borderRadius: frameRadius,
        // Gradient BORDER only: the border-box layer paints the ring (top white →
        // bottom black), the padding-box layer refills the interior with the dark
        // base so the 2px gap + canvas face stay untouched. Follows the rounded
        // corners (unlike border-image, which would square them off).
        border: '1px solid transparent',
        background: 'linear-gradient(#0b0c10, #0b0c10) padding-box, linear-gradient(to bottom, rgba(124, 124, 124, 0.5), rgba(0, 0, 0, 0.25)) border-box',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.55), -6px 2px 4px rgba(0, 0, 0, 0.8)',
        overflow: 'hidden',
        cursor: 'ns-resize',
        touchAction: 'none',
        flex: '0 0 auto',
        userSelect: 'none',
        padding: 2,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 2, width: 'calc(100% - 4px)', height: 'calc(100% - 4px)', borderRadius: Math.max(2, frameRadius - 3), display: 'block' }}
      />
    </div>
  )
}